import { useEffect } from 'react';
import { useSelector, useDispatch } from 'src/redux/store';
import { probeScopedAdmin } from 'src/redux/slices/scopedAdminUsers';

/**
 * Fail-closed capability probe for the "Managed Users" settings entry.
 *
 * Visibility of the scoped cross-practice admin surface is decided by the BE
 * (IsScopedPracticeAdmin), never by a client-side role guess. This hook is
 * mounted independently by SettingsMenu, SettingsPopover and MobileSettingsNav,
 * so it only dispatches when the determination is still unknown AND no probe is
 * already in flight — and `probeScopedAdmin` itself dedupes to a single live
 * request, so N concurrent mounts issue at most ONE `GET managed-users/?limit=1`.
 *
 * Returned value:
 *   - `null`  -> unknown (probe pending)  -> treated as NOT an admin (hidden)
 *   - `true`  -> BE returned 200          -> entry shown (sticky until an
 *                                            explicit invalidateScopedAdmin())
 *   - `false` -> BE returned 403 / error  -> entry hidden
 */
export default function useIsScopedAdmin() {
    const reduxDispatch = useDispatch();
    const isScopedAdmin = useSelector((state) => state.scopedAdminUsers?.isScopedAdmin);
    const isProbing = useSelector((state) => state.scopedAdminUsers?.isProbing);

    useEffect(() => {
        if (isScopedAdmin === null && !isProbing) {
            reduxDispatch(probeScopedAdmin());
        }
    }, [isScopedAdmin, isProbing, reduxDispatch]);

    return isScopedAdmin === true;
}
